import { CircularProgress, Grid } from "@mui/material";
import { Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../components/base/BasicDialog";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { useAgreement } from "../../../services/agreementsService";
import { AgreementCancellationDialog } from "../AgreementCancellationDialog";
import { AgreementKindLinkDisplay } from "./AgreementKindLinkDisplay";
import { AgreementModifyButton } from "./AgreementModifyButton";
import { AgreementPassportTabs } from "./AgreementPassportTabs";
import IconButtonWithTooltip from "../../../components/button/IconButtonWithTooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { USER_ROLES } from "../../../helpers/constants";
import { useHasRole } from "../../../hooks/auth/useCheckPermission";


export const AgreementPassportDialog = (props) => {
    const { open, onClose, agreementId } = props;
    const { t } = useTranslation();
    const navigate = useNavigate()
    const { data: agreement, isLoading } = useAgreement(agreementId);
    const { openDialog } = useDialogBetter();

    const hasPermissionToEditAgreement = useHasRole([
        USER_ROLES.ADMIN, 
        USER_ROLES.SALES, 
        USER_ROLES.SALES_LEGAL, 
        USER_ROLES.SALES_MANAGER, 
        USER_ROLES.SALES_MARKETING
    ]) 



    const agreementApartment = useMemo(()=>{
        if (!agreement) return null

        return agreement?.products?.find((agreementProduct)=>agreementProduct.product?.is_apartment === true)?.product
    }, [agreement])

    const openCancelAgreementDialog = () => {
        openDialog({
            Component: AgreementCancellationDialog,
            componentProps: {
                agreementId: agreementId,
                agreementPaymentPaidAmount: agreement.reservation_payment_paid_amount,
                onSuccess: onClose,
            },
        });
    };

    if (!agreement) {
        return <CircularProgress />;
    }

    const isAgreementCancelled = ["Cancelled", "agreementCancelled"].includes(
        // handle legacy data
        agreement.agreement_status.status_key,
    );
    const isAgreementTransformed = agreement.agreement_status.status_key === "Transformed";
    const isAgreementFinalSettlement = agreement.agreement_final_settlement

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            // title={`${t("agreements.AgreementPassportDialog.title")} ${agreement.agreement_nr}`}
            headerComponent={
                <Grid
                    container
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    sx={{ p: 1}}
                >   
                    <Grid item xs={5}>
                    {hasPermissionToEditAgreement &&
                        <IconButtonWithTooltip
                            title={t("back")}
                            onClick={()=>navigate("/agreements/")}
                        >
                            <ArrowBackIcon sx={{ color: "var(--primary)" }} fontSize="large" />
                        </IconButtonWithTooltip>}
                    </Grid>
                    <Grid item xs={6} style={{display: "flex", alignItems:"center"}}>
                        <Typography variant={"h2"} sx={{ color: "var(--primary)", fontWeight: "500", fontSize: "24px"}}>
                            {`${t("agreements.AgreementPassportDialog.title")} ${agreement.agreement_nr}`}
                        </Typography> 
                        <Typography variant={"h2"} sx={{ color: "var(--primary)", fontWeight: "500", border: "solid var(--primary) 2px", fontSize: "24px", marginLeft:"10px", paddingInline: "5px"}}>
                            {`${agreementApartment?.product_number || ""}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={1}></Grid>
                </Grid>
            }
            minWidth={800}
            maxWidth={"xl"}
            alwaysFullScreen={true}
            showDialogActions
        >
            <Grid container direction="column" spacing={2}>
                <Grid item xs={12}>
                    <AgreementKindLinkDisplay
                        currentAgreementKindKey={agreement.agreement_kind.agrekind_key}
                        reservationAgreementId={agreement.reservation_agreement_id}
                        preinitialAgreementId={agreement.preliminary_agreement_id}
                        developmentAgreementId={agreement.development_agreement_id}
                        salesAgreementId={agreement.sale_agreement_id}
                        agreementBooking={agreement.agreement_booking}
                    />
                </Grid>
                <Grid item>
                    <AgreementPassportTabs
                        agreement={agreement}
                        readOnly={isAgreementTransformed || isAgreementCancelled || !hasPermissionToEditAgreement}
                    />
                </Grid>

                <Grid item xs={12} container spacing={2} justifyContent="flex-end">
                    <Grid item>
                        <Button
                            onClick={openCancelAgreementDialog}
                            variant="outlined"
                            color="error"
                            disabled={isAgreementCancelled || isAgreementTransformed || isAgreementFinalSettlement || !hasPermissionToEditAgreement}
                        >
                            {t("agreements.AgreementPassportDialog.cancelAgreement")}
                        </Button>
                    </Grid>
                    <Grid item>
                        <AgreementModifyButton
                            disable={isAgreementCancelled || !hasPermissionToEditAgreement}
                            agreement={agreement}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </BasicDialog>
    );
};
